<div class="min-h-screen bg-surface-50">
  <!-- Header -->
  <div
    class="flex items-center justify-between px-4 py-4 md:px-8 md:py-5 bg-white border-b border-slate-100 gap-3"
  >
    <div class="flex items-center gap-3 min-w-0">
      <button
        type="button"
        (click)="goBack()"
        class="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer shrink-0"
      >
        <img src="/icons/arrow-left-icon.svg" class="w-4 h-4" alt="Back" />
      </button>
      <div class="min-w-0">
        @if (loading()) {
          <div class="h-5 w-32 md:w-48 bg-slate-100 rounded animate-pulse mb-1"></div>
          <div class="h-3 w-24 md:w-32 bg-slate-100 rounded animate-pulse"></div>
        } @else {
          <div class="flex items-center gap-2 flex-wrap">
            <h1 class="text-sm font-bold text-navy-900 truncate">{{ rollup()?.name ?? '' }}</h1>
            <span
              class="inline-block px-2 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide border shrink-0"
              [ngClass]="{
                'border-accent-500 text-accent-500': type() === 'situation',
                'border-purple-500 text-purple-500': type() === 'response',
              }"
            >
              {{ type() }}
            </span>
          </div>
          <p class="text-xs text-slate-500 mt-0.5">Rollup Taxonomies</p>
        }
      </div>
    </div>

    <div class="flex items-center gap-2 shrink-0">
      @if (editMode()) {
        <button
          type="button"
          (click)="onCancel()"
          [disabled]="saving()"
          class="px-3 md:px-5 py-2 text-xs md:text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="onSave()"
          [disabled]="saving()"
          class="px-3 md:px-5 py-2 text-xs md:text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {{ saving() ? 'Saving...' : 'Save Changes' }}
        </button>
      } @else {
        <button
          type="button"
          (click)="confirmDelete.set(true)"
          [disabled]="loading()"
          class="flex items-center gap-1.5 px-3 md:px-5 py-2 text-xs md:text-sm font-semibold text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
        >
          <img src="/icons/trash-icon.svg" class="w-3.5 h-3.5 md:w-4 md:h-4" alt="" />
          <span class="hidden sm:inline">Delete</span>
        </button>
        <button
          type="button"
          (click)="onEdit()"
          [disabled]="loading()"
          class="flex items-center gap-1.5 px-3 md:px-5 py-2 text-xs md:text-sm font-semibold text-navy-900 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
        >
          <img src="/icons/edit-icon.svg" class="w-3.5 h-3.5 md:w-4 md:h-4" alt="" />
          <span class="hidden sm:inline">Edit</span>
        </button>
      }
    </div>
  </div>

  @if (loading()) {
    <div class="flex items-center justify-center py-20 text-xs text-slate-400">Loading...</div>
  } @else {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 md:p-8">
      <!-- Left: Source Taxonomy Library -->
      <div>
        <p class="text-xs font-bold text-navy-900 mb-4">Source Taxonomy Library</p>

        @if (loadingSource()) {
          <div class="flex items-center justify-center py-10 text-xs text-slate-400">
            Loading source library...
          </div>
        } @else if (!sourceGroups().length) {
          <div class="text-xs text-slate-400 py-6">No source taxonomies found.</div>
        } @else {
          <div class="flex flex-col gap-3">
            @for (group of sourceGroups(); track group.taxonomyId) {
              <div class="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <!-- Group header -->
                <button
                  type="button"
                  (click)="toggleGroup(group.taxonomyId)"
                  class="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer text-left"
                >
                  <img src="/icons/folder-icon.svg" class="w-4 h-4 shrink-0 opacity-60" alt="" />
                  <span class="text-sm font-semibold text-navy-900 flex-1">{{
                    group.taxonomyName
                  }}</span>
                  <img
                    src="/icons/chevron-down-icon.svg"
                    class="w-3.5 h-3.5 shrink-0 transition-transform"
                    [class.-rotate-180]="group.expanded"
                    alt=""
                  />
                </button>

                <!-- Categories -->
                @if (group.expanded && group.categories.length > 0) {
                  <div class="border-t border-slate-100">
                    @for (cat of group.categories; track cat.id) {
                      <div
                        class="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 transition-colors"
                        [class.cursor-pointer]="editMode()"
                        [class.hover:bg-blue-50]="editMode()"
                        (click)="toggleCategory(cat.id)"
                      >
                        <!-- Checkbox -->
                        <div
                          class="w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-colors"
                          [ngClass]="
                            isChecked(cat.id)
                              ? 'bg-accent-500 border-accent-500'
                              : 'border-slate-300 bg-white'
                          "
                        >
                          @if (isChecked(cat.id)) {
                            <img src="/icons/check-white-icon.svg" class="w-2.5 h-2.5" alt="" />
                          }
                        </div>
                        <img
                          src="/icons/tag-icon.svg"
                          class="w-3.5 h-3.5 shrink-0 opacity-50"
                          alt=""
                        />
                        <span
                          class="text-xs flex-1"
                          [ngClass]="
                            isChecked(cat.id) ? 'text-accent-500 font-medium' : 'text-navy-900'
                          "
                        >
                          {{ cat.name }}
                        </span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Right: Mapped Rollup Members -->
      <div>
        <div class="flex items-center justify-between mb-4">
          <p class="text-xs font-bold text-navy-900">Mapped Rollup Members</p>
          @if (mappedCategories().length > 0) {
            <span
              class="px-3 py-1 rounded-full bg-accent-50 text-accent-600 text-2xs font-semibold border border-accent-200"
            >
              {{ mappedCategories().length }} Active
              {{ mappedCategories().length === 1 ? 'Member' : 'Members' }}
            </span>
          }
        </div>

        @if (!mappedCategories().length) {
          <div class="flex flex-col items-center justify-center py-16 text-center">
            <img src="/icons/tag-icon.svg" class="w-8 h-8 opacity-20 mb-3" alt="" />
            <p class="text-xs text-slate-400">No categories mapped yet.</p>
            @if (!editMode()) {
              <p class="text-2xs text-slate-300 mt-1">Click Edit to start mapping categories.</p>
            }
          </div>
        } @else {
          <div class="flex flex-col gap-2">
            @for (cat of mappedCategories(); track cat.id) {
              <div
                class="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 bg-white"
              >
                <img src="/icons/tag-icon.svg" class="w-4 h-4 shrink-0 opacity-50" alt="" />
                <span class="text-xs font-medium text-navy-900 flex-1">{{ cat.name }}</span>
                @if (editMode()) {
                  <button
                    type="button"
                    (click)="removeCategory(cat.id)"
                    class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                  >
                    <img
                      src="/icons/close-icon.svg"
                      class="w-2.5 h-2.5 opacity-40 hover:opacity-70"
                      alt="Remove"
                    />
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  }
</div>

@if (confirmDelete()) {
  <app-modal
    title="Delete Rollup"
    subtitle="This action cannot be undone"
    (closed)="confirmDelete.set(false)"
  >
    <p class="text-sm text-slate-600">
      Are you sure you want to delete
      <span class="font-semibold text-navy-900">{{ rollup()?.name }}</span
      >? All mapped categories will be removed.
    </p>

    <div modal-footer class="flex items-center justify-end gap-3">
      <button
        type="button"
        (click)="confirmDelete.set(false)"
        [disabled]="deleting()"
        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="onDelete()"
        [disabled]="deleting()"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-red-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ deleting() ? 'Deleting...' : 'Delete Rollup' }}
      </button>
    </div>
  </app-modal>
}
